import { createTheme } from '@mui/material/styles';
import { isIS } from '@mui/material/locale';

const theme = createTheme({
  palette: {
    primary: { main: '#163f65', light: '#eaf1f7', dark: '#102f4d' },
    secondary: { main: '#147568' },
    success: { main: '#23734f', light: '#edf7f1', dark: '#175338' },
    warning: { main: '#8a5b0a', light: '#fff8e6', dark: '#674308' },
    background: { default: '#f3f6f8', paper: '#ffffff' },
    text: { primary: '#1d3043', secondary: '#526578' },
    divider: '#dce4eb',
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h4: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.035em' },
    h5: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em' },
    h6: { fontSize: '1.125rem', fontWeight: 650 },
    subtitle1: { fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.65 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: { styleOverrides: {
      body: { minWidth: 320 },
      '::selection': { backgroundColor: '#d9e9f3' },
      ':focus-visible': { outline: '3px solid #147568', outlineOffset: 3 },
    } },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { minHeight: 44, padding: '10px 20px' } },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, fontSize: '0.75rem' } } },
    MuiInputAdornment: { styleOverrides: { root: { color: '#526578', '& svg': { display: 'block', flexShrink: 0 } } } },
    MuiOutlinedInput: { styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#b6c4d0' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
        '&.Mui-focused': { boxShadow: '0 0 0 3px #eaf1f7' },
        '&.Mui-disabled': { backgroundColor: theme.palette.background.default },
      }),
      input: { padding: '14px', fontSize: '1rem' },
    } },
    MuiInputLabel: { styleOverrides: { root: { color: '#526578' } } },
    MuiTableContainer: { styleOverrides: {
      root: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}`, borderRadius: 10 }),
    } },
    MuiTableCell: { styleOverrides: {
      root: ({ theme }) => ({ padding: '16px 20px', borderColor: theme.palette.divider }),
      head: { backgroundColor: '#f0f5f8', color: '#354e65', fontWeight: 650, whiteSpace: 'nowrap' },
      body: { fontVariantNumeric: 'tabular-nums' },
    } },
    MuiTableRow: { styleOverrides: { root: {
      '&:last-child td, &:last-child th': { borderBottom: 0 },
      '&.MuiTableRow-hover:hover': { backgroundColor: '#f7fafc' },
    } } },
    MuiStepLabel: { styleOverrides: { label: {
      fontSize: '0.8125rem',
      '&.Mui-active': { fontWeight: 700 },
      '&.Mui-completed': { color: '#147568', fontWeight: 600 },
    } } },
    MuiStepIcon: { styleOverrides: { root: {
      fontSize: 30, color: '#dce4eb',
      '&.Mui-completed': { color: '#147568' },
      '&.Mui-active .MuiStepIcon-text': { fill: '#fff' },
    }, text: { fill: '#1d3043', fontWeight: 600 } } },
    MuiStepConnector: { styleOverrides: {
      alternativeLabel: { top: 15 },
      line: { borderColor: '#dce4eb', borderTopWidth: 2 },
      root: { '&.Mui-completed .MuiStepConnector-line': { borderColor: '#147568' } },
    } },
    MuiAlert: { styleOverrides: { root: { borderRadius: 10, alignItems: 'center' } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 14 }, paperFullScreen: { borderRadius: 0 } } },
    MuiDialogTitle: { styleOverrides: { root: { padding: '24px 24px 16px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', gap: 8, flexWrap: 'wrap' } } },
    MuiMenu: { styleOverrides: { paper: { boxShadow: '0 12px 40px rgba(22, 63, 101, 0.16)', border: '1px solid #dce4eb' } } },
    MuiMenuItem: { styleOverrides: { root: { minHeight: 44, fontSize: '0.9375rem' } } },
    MuiLinearProgress: { styleOverrides: { root: { height: 6, borderRadius: 3, backgroundColor: '#dce4eb' } } },
  },
}, isIS);

export default theme;
